import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import validate from 'validate.js';
import { TimeInputCustom } from "pages/TimeSheetManage/WeeklyTimeSheet/component";

const schema = {
  comment: {
    length: {
      maximum: 200,
      message: "cannot be more than 200 characters",
    },
  },
};


const EditTimeModal = ({ show, handleClose, rowData, onUpdate }) => {
  const dispatch = useDispatch();
  const weeklyTimesheetState = useSelector((state) => state.weeklyTimesheetState);
  const { weeklyTimesheetRecord, validation_error } = weeklyTimesheetState;

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      mon: { shift: 0, time: 0 },
      tue: { shift: 0, time: 0 },
      wed: { shift: 0, time: 0 },
      thu: { shift: 0, time: 0 },
      fri: { shift: 0, time: 0 },
      sat: { shift: 0, time: 0 },
      sun: { shift: 0, time: 0 },
      comment: "",
    },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    if (weeklyTimesheetRecord) {
      const newValues = {
        category_task: rowData?.category_task || "N/A",
        employee: rowData?.employee || "N/A",
        week_ending: rowData?.week_ending || "N/A",
        weekly_timesheet_id: weeklyTimesheetRecord.id,
        comment: weeklyTimesheetRecord.comments || "",
        mon: { shift: 0, time: 0 },
        tue: { shift: 0, time: 0 },
        wed: { shift: 0, time: 0 },
        thu: { shift: 0, time: 0 },
        fri: { shift: 0, time: 0 },
        sat: { shift: 0, time: 0 },
        sun: { shift: 0, time: 0 },
      };

      const dayMapping = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

      weeklyTimesheetRecord.details?.forEach(detail => {
        const workDate = new Date(detail.work_date + 'T00:00:00'); // Avoid timezone issues
        const dayName = dayMapping[workDate.getDay()];
        if (dayName) {
          newValues[dayName] = {
            shift: detail.hours || 0,
            time: detail.minutes || 0,
            id: detail.id || null
          };
        }
      });

      setFormState(fs => ({
        ...fs,
        values: newValues
      }));
    }
  }, [weeklyTimesheetRecord, rowData]);

  useEffect(() => {
    if (validation_error) {
      setFormState((fs) => ({
        ...fs,
        isValid: false,
        errors: validation_error || {},
      }));
    }
  }, [validation_error]);

  useEffect(() => {
    const errors = validate(formState.values, schema);

    // Custom validation for time entries
    const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    days.forEach(day => {
      const dayValue = formState.values[day];
      if (dayValue) {
        const hours = parseInt(dayValue.shift, 10);
        const minutes = parseInt(dayValue.time, 10);
        if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59 || (hours === 24 && minutes > 0)) {
          if (!errors) errors = {};
          errors[day] = ["Invalid time entry. Hours must be 0-24 and minutes 0-59."];
        }
      }
    });

    setFormState(fs => ({
      ...fs,
      isValid: !errors,
      errors: errors || {}
    }));
  }, [formState.values]);


  const handleValueChange = (day, field, value) => {
    const fieldName = field === 'hours' ? 'shift' : 'time';
    setFormState(fs => ({
      ...fs,
      values: {
        ...fs.values,
        [day]: {
          ...fs.values[day],
          [fieldName]: value
        }
      },
      touched: {
        ...fs.touched,
        [day]: true
      }
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(fs => ({
      ...fs,
      values: { ...fs.values, [name]: value },
      touched: { ...fs.touched, [name]: true }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formState.isValid) {
      // Optionally touch all fields to show errors
      const touched = Object.keys(formState.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setFormState(fs => ({ ...fs, touched }));
      return;
    }

    const dataToUpdate = {
      id: formState.values.weekly_timesheet_id,
      comment: formState.values.comment,
      details: [],
    };

    const dayMapping = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const weekStartDate = new Date(weeklyTimesheetRecord.week_start_date + 'T00:00:00');
    dayMapping.forEach((day, index) => {
      const dayValue = formState.values[day];
      if (dayValue && (parseInt(dayValue.shift, 10) > 0 || parseInt(dayValue.time, 10) > 0)) {
        const workDate = new Date(weekStartDate);
        workDate.setDate(weekStartDate.getDate() + index);

        const yyyy = workDate.getFullYear();
        const mm = String(workDate.getMonth() + 1).padStart(2, '0'); // month is zero-based
        const dd = String(workDate.getDate()).padStart(2, '0');
        const formattedDate = `${yyyy}-${mm}-${dd}`;


        dataToUpdate.details.push({
          id: dayValue.id,
          work_date: formattedDate, // YYYY-MM-DD
          hours: parseInt(dayValue.shift, 10),
          minutes: parseInt(dayValue.time, 10),
          total_time: parseFloat(dayValue.shift || 0) + parseFloat(dayValue.time || 0) / 60,
        });
      }
    });

    onUpdate(dataToUpdate);
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {" "}
          <h2 className="font-16 font-weight-bold heading-color mb-0">
            Edit Time
          </h2>{" "}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* --- Row 1: Category/Task, Employee, Calendar --- */}
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group controlId="formcategory_task">
                <Form.Label className="label-input">Category/Task</Form.Label>
                <Form.Control
                  type="text"
                  name="category_task"
                  placeholder="Category/Task"
                  value={formState.values.category_task || ''}
                  className="input-field"
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="formEmployee">
                <Form.Label className="label-input">Employee</Form.Label>
                <Form.Control
                  type="text"
                  name="employee"
                  placeholder="Employee"
                  value={formState.values.employee || ''}
                  className="input-field"
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="formCalendar">
                <Form.Label className="label-input">Week Ending</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    name="calendarDate"
                    value={formState.values.week_ending || ''}
                    readOnly
                    disabled
                    className="input-field"
                  />
                  <span
                    style={{ cursor: "pointer", position: 'absolute', top: "7px", right: "10px" }}
                  >
                    <img src="/assets/images/calendar-icon.svg" alt="" />
                  </span>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* --- Row 2: Monday, Tuesday, Wednesday --- */}
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="formMon">
                <Form.Label className="label-input">Monday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.mon.shift, minutes: formState.values.mon.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("mon", field, value)}
                />
                {hasError('mon') && (
                  <Form.Text className="text-danger">
                    {formState.errors.mon[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formTue">
                <Form.Label className="label-input">Tuesday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.tue.shift, minutes: formState.values.tue.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("tue", field, value)}
                />
                {hasError('tue') && (
                  <Form.Text className="text-danger">
                    {formState.errors.tue[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formWed">
                <Form.Label className="label-input">Wednesday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.wed.shift, minutes: formState.values.wed.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("wed", field, value)}
                />
                {hasError('wed') && (
                  <Form.Text className="text-danger">
                    {formState.errors.wed[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formThu">
                <Form.Label className="label-input">Thursday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.thu.shift, minutes: formState.values.thu.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("thu", field, value)}
                />
                {hasError('thu') && (
                  <Form.Text className="text-danger">
                    {formState.errors.thu[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formFri">
                <Form.Label className="label-input">Friday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.fri.shift, minutes: formState.values.fri.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("fri", field, value)}
                />
                {hasError('fri') && (
                  <Form.Text className="text-danger">
                    {formState.errors.fri[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formSat">
                <Form.Label className="label-input">Saturday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.sat.shift, minutes: formState.values.sat.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("sat", field, value)}
                />
                {hasError('sat') && (
                  <Form.Text className="text-danger">
                    {formState.errors.sat[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formSun">
                <Form.Label className="label-input">Sunday</Form.Label>
                <TimeInputCustom
                  value={{ hours: formState.values.sun.shift, minutes: formState.values.sun.time }}
                  handleValueChange={(day, dayIndex, { field, value }) => handleValueChange("sun", field, value)}
                />
                {hasError('sun') && (
                  <Form.Text className="text-danger">
                    {formState.errors.sun[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* --- Row 5: Comment --- */}
          <Row className="mb-4">
            <Col xs={12}>
              <Form.Group controlId="formComment">
                <Form.Label className="label-input">Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comment"
                  style={{
                    borderRadius: "10px",
                    height: "85px",
                    border: "1px solid rgb(206, 212, 218)",
                    padding: "4px 12px",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "rgb(97, 121, 115)",
                  }}
                  placeholder="Type here..."
                  value={formState.values.comment || ''}
                  onChange={handleChange}
                />
                {hasError('comment') && (
                  <Form.Text className="text-danger">
                    {formState.errors.comment[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-inline">
        <div className="d-flex gap-3">
          <button
            variant="none"
            className="c-b-btn w-100"
            onClick={handleClose}
            style={{
              borderColor: "#adb5bd",
              padding: "10px 20px",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="n-u-btn w-100"
            disabled={!formState.isValid}
            style={{
              backgroundColor: "#007bff", // Updated to a standard primary color
              borderColor: "#007bff",
              color: "white",
              padding: "10px 20px",
            }}
          >
            Update
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditTimeModal;